import React, { useCallback, useMemo, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { Document, Page, pdfjs } from 'react-pdf'

import styled from 'styled-components'
import { Modal } from 'antd'
import * as Icons from '@ant-design/icons'

import { ColourButton } from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

if (pdfjs && pdfjs.GlobalWorkerOptions) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
}

export function DocumentViewer({
  documentName,
  documentURL,
  documentPageNumber = 1,
  onCancel
}) {
  const dispatch = useDispatch()
  const params = useParams()

  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const draggleRef = useRef<HTMLDivElement>(null)
  const [pdfPages, setPDFPages] = useState(1)
  const [pdfCurrentPage, setPDFCurrentPage] = useState(documentPageNumber)
  const [previewRotation, setPreviewRotation] = useState(0)
  const [zoomedIn, setZoomedIn] = useState(false)

  const DocumentOptions = useMemo(
    () => ({
      wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`
    }),
    []
  )

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  const rotationDegrees = [0, 90, 180, 270]

  const handlePDFLoadSuccess = pdf => {
    setPDFPages(pdf?.numPages)
  }

  const handleRotationChange = turnDirection => {
    if (turnDirection === 1) {
      const rotationIndex = previewRotation + 1
      setPreviewRotation(
        rotationIndex >= rotationDegrees.length ? 0 : rotationIndex
      )
    } else if (turnDirection === -1) {
      const rotationIndex = previewRotation - 1
      setPreviewRotation(
        rotationIndex < 0 ? rotationDegrees.length - 1 : rotationIndex
      )
    }
  }

  const handleZoom = () => {
    setZoomedIn(prev => !prev)
  }

  const changePage = val => {
    setPDFCurrentPage(prev => prev + val)
  }

  const jumpToPage = pageNumber => {
    if (pageNumber >= 1 && pageNumber <= pdfPages) {
      setPDFCurrentPage(pageNumber)
    }
  }

  const handleCancel = () => {
    // Close the modal
    onCancel()
  }

  const onDownloaded = useCallback(() => {
    const SNIPPET_DOCUMENT_DOWNLOAD =
      'frontend_records_design_match_document_download'

    dispatch(
      trackMixpanel(SNIPPET_DOCUMENT_DOWNLOAD, {
        account_id: params.accountId,
        project_id: params.projectId,
        record_id: params.recordId,
        document_name: documentName
      })
    )
  }, [
    dispatch,
    params.accountId,
    params.projectId,
    params.recordId,
    documentName
  ])

  return (
    <Modal
      title={
        <div
          style={{
            width: '100%',
            cursor: 'move'
          }}
        >{`${documentName} Preview`}</div>
      }
      open={true}
      width={zoomedIn ? '66.7vw' : 800}
      styles={{ body: { textAlign: 'center' } }}
      onOk={() => {
        window.location.replace(documentURL)
        onDownloaded()
      }}
      onCancel={handleCancel}
      transitionName=""
      footer={[
        <StyledControls key={`previewControls-${documentName}`}>
          <div>
            <ColourButton.Basic
              type="button"
              onClick={() => handleRotationChange(-1)}
              style={{ display: 'inline-block', marginRight: '.75rem' }}
              title="Rotate left"
            >
              <Icons.RotateLeftOutlined />
            </ColourButton.Basic>

            <ColourButton.Basic
              type="button"
              onClick={() => handleRotationChange(1)}
              style={{ display: 'inline-block', marginRight: '.75rem' }}
              title="Rotate right"
            >
              <Icons.RotateRightOutlined />
            </ColourButton.Basic>

            <ColourButton.Basic
              type="button"
              onClick={handleZoom}
              style={{ display: 'inline-block' }}
              title="Zoom in/out"
            >
              {!zoomedIn && <Icons.ZoomInOutlined />}
              {zoomedIn && <Icons.ZoomOutOutlined />}
            </ColourButton.Basic>
          </div>
          {pdfPages > 1 && (
            <PageControls>
              <PageControlButton
                type="button"
                onClick={() => jumpToPage(1)}
                style={{ marginRight: '.75rem' }}
                title="Previous page"
                disabled={pdfCurrentPage <= 1}
              >
                <Icons.BackwardOutlined
                  style={{ fontSize: '1.25rem', margin: '1px -2px -1px -4px' }}
                />
              </PageControlButton>
              <PageControlButton
                type="button"
                onClick={() => changePage(-1)}
                style={{
                  marginRight: '.75rem'
                }}
                title="Previous page"
                disabled={pdfCurrentPage <= 1}
              >
                <Icons.CaretLeftOutlined />
              </PageControlButton>
              {pdfCurrentPage} of {pdfPages}
              <PageControlButton
                type="button"
                onClick={() => changePage(1)}
                style={{
                  marginLeft: '.75rem'
                }}
                title="Next page"
                disabled={pdfCurrentPage >= pdfPages}
              >
                <Icons.CaretRightOutlined />
              </PageControlButton>
              <PageControlButton
                type="button"
                onClick={() => jumpToPage(pdfPages)}
                style={{ marginLeft: '.75rem' }}
                title="Next page"
                disabled={pdfCurrentPage >= pdfPages}
              >
                <Icons.ForwardOutlined
                  style={{ fontSize: '1.25rem', margin: '1px -4px -1px -2px' }}
                />
              </PageControlButton>
            </PageControls>
          )}
          <ColourButton.Action
            type="button"
            onClick={() => {
              window.location.replace(documentURL)
              onDownloaded()
            }}
            style={{ display: 'inline-block' }}
          >
            Download
          </ColourButton.Action>
        </StyledControls>
      ]}
      modalRender={modal => (
        <Draggable
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
    >
      <StyledDocument
        file={documentURL}
        rotate={rotationDegrees[previewRotation]}
        onLoadSuccess={handlePDFLoadSuccess}
        options={DocumentOptions}
      >
        <StyledPDFPage
          pageNumber={pdfCurrentPage}
          renderAnnotationLayer={false}
          renderTextLayer={false}
          scale={2.0}
          $zoomedIn={zoomedIn}
        />
      </StyledDocument>
    </Modal>
  )
}

const StyledDocument = styled(Document)`
  max-height: ${({ rotate }) =>
    rotate === 0 || rotate === 180 ? 'calc(100vh - 285px)' : '800px'};
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
  overflow-y: auto;
`

interface PDFPageProps {
  $zoomedIn: boolean
}
const StyledPDFPage = styled(Page)<PDFPageProps>`
  position: relative;
  max-height: calc(100vh - 285px);
  max-width: ${({ $zoomedIn }) => ($zoomedIn ? '66.7vw' : '752px')};
  min-width: ${({ $zoomedIn }) =>
    $zoomedIn ? '66.7vw !important' : '752px !important'};
  margin-top: 4px;
  overflow: none;

  & CANVAS {
    height: ${({ $zoomedIn }) =>
      $zoomedIn ? '100%  !important' : 'calc(100vh - 289px) !important'};
    width: ${({ $zoomedIn }) =>
      $zoomedIn ? 'calc(66.7vw - 40px) !important' : '100% !important'};
    object-fit: ${({ $zoomedIn }) => ($zoomedIn ? 'contain' : 'scale-down')};
  }
`

const StyledControls = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`
const PageControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
const PageControlButton = styled(ColourButton.Basic)`
  display: inline-block;
  padding: 4px 8px;
`
